import * as THREE from 'three';
import type { SignalHeadDecor } from '../decor';
import type { SignalLamps, SignalLights } from '../signalControl';
import { PAVE_Y } from './ground';
import { ChunkedBuilders, type RGB } from './meshBuilder';
import { T, tileUV } from './textures';

/**
 * Traffic-light visuals. Poles, housings and backboards are static merged geometry; the lenses are
 * a single InstancedMesh whose per-instance colours are driven every frame by the SignalService.
 */
const WHITE = tileUV(T.white, 40);
const HOUSING: RGB = [0.05, 0.05, 0.05];
const LENS_Y = [3.02, 2.7, 2.38]; // red, amber, green (top -> bottom)
const ON: RGB[] = [
  [6.0, 0.35, 0.18],
  [6.0, 2.6, 0.2],
  [0.3, 5.5, 2.4],
];
const OFF: RGB[] = [
  [0.09, 0.02, 0.015],
  [0.09, 0.06, 0.01],
  [0.01, 0.07, 0.04],
];

export function buildSignalStatics(heads: SignalHeadDecor[], cb: ChunkedBuilders): void {
  for (const h of heads) {
    const mb = cb.get('metal', h.pos.x, h.pos.z);
    const cs = Math.cos(h.heading);
    const sn = Math.sin(h.heading);
    const P = (lx: number, lz: number): [number, number] => [h.pos.x + lx * cs + lz * sn, h.pos.z - lx * sn + lz * cs];
    mb.cylinder(h.pos.x, h.pos.z, PAVE_Y, PAVE_Y + 3.3, 0.07, 0.06, 6, [0.12, 0.12, 0.12], WHITE);
    // black/white banded base (period detail)
    mb.cylinder(h.pos.x, h.pos.z, PAVE_Y + 0.6, PAVE_Y + 1.0, 0.075, 0.075, 6, [0.85, 0.85, 0.82], WHITE, { top: false });
    const [hx, hz] = P(0, 0.08);
    mb.box(hx, PAVE_Y + 2.15, hz, 0.34, 1.05, 0.26, h.heading, HOUSING, WHITE);
    // backboard with white border
    const [bx, bz] = P(0, -0.08);
    mb.box(bx, PAVE_Y + 2.05, bz, 0.62, 1.25, 0.03, h.heading, [0.8, 0.8, 0.78], WHITE);
    const [bx2, bz2] = P(0, -0.06);
    mb.box(bx2, PAVE_Y + 2.1, bz2, 0.54, 1.15, 0.03, h.heading, [0.03, 0.03, 0.03], WHITE);
    // hoods
    for (const y of LENS_Y) {
      const [vx, vz] = P(0, 0.27);
      mb.box(vx, PAVE_Y + y - 0.13 + 0.25, vz, 0.28, 0.03, 0.16, h.heading, HOUSING, WHITE);
    }
  }
}

export interface SignalsView {
  update(): void;
  /** A car knocked this head's pole over: hide its lenses and road reflection for good. */
  knockOut(index: number): void;
  /** How lit the street lamps are (mood `lamps`: ~0.06 by day, 1 at dusk, 1.1 at night). */
  setLevel(lamps: number): void;
  /** Road wetness 0..1: a wet road picks the signal colour up by day as well. */
  setWetness(w: number): void;
  dispose(): void;
}

export function createSignalsView(scene: THREE.Scene, heads: SignalHeadDecor[], signals: SignalLights, glowTex: THREE.Texture): SignalsView {
  const geo = new THREE.CylinderGeometry(0.14, 0.14, 0.05, 12).rotateX(Math.PI / 2);
  const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const mesh = new THREE.InstancedMesh(geo, mat, heads.length * 3);
  mesh.name = 'signal-lenses';
  mesh.frustumCulled = false;
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const one = new THREE.Vector3(1, 1, 1);
  const p = new THREE.Vector3();
  const col = new THREE.Color();
  heads.forEach((h, i) => {
    q.setFromAxisAngle(up, h.heading);
    const cs = Math.cos(h.heading);
    const sn = Math.sin(h.heading);
    for (let k = 0; k < 3; k++) {
      const lz = 0.22;
      p.set(h.pos.x + lz * sn, PAVE_Y + LENS_Y[k]!, h.pos.z + lz * cs);
      m.compose(p, q, one);
      mesh.setMatrixAt(i * 3 + k, m);
      const c = OFF[k]!;
      mesh.setColorAt(i * 3 + k, col.setRGB(c[0], c[1], c[2]));
    }
  });
  scene.add(mesh);

  // Coloured reflection of each signal on the wet road in its approach lane (readable from above).
  const rGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  const rMat = new THREE.MeshBasicMaterial({ map: glowTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
  const refl = new THREE.InstancedMesh(rGeo, rMat, heads.length);
  refl.name = 'signal-reflections';
  refl.frustumCulled = false;
  refl.renderOrder = 3;
  const s3 = new THREE.Vector3();
  heads.forEach((h, i) => {
    const ox = Math.sin(h.heading);
    const oz = Math.cos(h.heading);
    // out = direction away from the junction; lane centre is to (out.z, -out.x) of the kerbside head
    p.set(h.pos.x + ox * 2.0 + oz * 2.4, 0.03, h.pos.z + oz * 2.0 - ox * 2.4);
    q.setFromAxisAngle(up, h.heading);
    m.compose(p, q, s3.set(2.6, 1, 4.2));
    refl.setMatrixAt(i, m);
    refl.setColorAt(i, col.setRGB(0, 0, 0));
  });
  scene.add(refl);
  const reflState = new Int8Array(heads.length).fill(-1);
  // The coloured pools are a glow on the road: they show at dusk and at night, and by day only on a wet
  // road (a dry road in daylight does not pick a signal lamp up at all). Hidden when off: no draw call.
  let lampLevel = 1;
  let wet = 0;
  const applyPools = (): void => {
    const dark = Math.min(1, Math.max(0, (lampLevel - 0.15) / 0.7));
    // Coloured spill on the road, softer than the lens itself so a junction doesn't wear a red blob.
    const o = Math.max(0.72 * dark, 0.35 * wet);
    rMat.opacity = o;
    refl.visible = o > 0.01;
  };
  const REFL: RGB[] = [
    [0.55, 0.05, 0.03],
    [0.6, 0.3, 0.03],
    [0.05, 0.5, 0.25],
    [0.6, 0.18, 0.03],
  ];
  const state = new Int8Array(heads.length * 3).fill(-1);
  const lampsOut: SignalLamps = { red: false, amber: false, green: false };
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  return {
    knockOut(i) {
      if (i < 0 || i >= heads.length) return;
      for (let k = 0; k < 3; k++) mesh.setMatrixAt(i * 3 + k, zero);
      refl.setMatrixAt(i, zero);
      mesh.instanceMatrix.needsUpdate = true;
      refl.instanceMatrix.needsUpdate = true;
    },
    setLevel(lamps) {
      lampLevel = lamps;
      applyPools();
    },
    setWetness(w) {
      wet = w;
      applyPools();
    },
    update() {
      // One lamps() read per head per frame, into a reused record: the two passes used to allocate three
      // objects per head per frame between them (docs/PERF.md "Phone pass").
      let dirty = false;
      let rDirty = false;
      for (let i = 0; i < heads.length; i++) {
        const h = heads[i]!;
        const l = signals.lamps(h.intersectionId, h.edgeId, lampsOut);
        for (let k = 0; k < 3; k++) {
          const v = (k === 0 ? l.red : k === 1 ? l.amber : l.green) ? 1 : 0;
          if (state[i * 3 + k] === v) continue;
          state[i * 3 + k] = v;
          const c = (v ? ON : OFF)[k]!;
          mesh.setColorAt(i * 3 + k, col.setRGB(c[0], c[1], c[2]));
          dirty = true;
        }
        const rk = l.green ? 2 : l.red && l.amber ? 3 : l.amber ? 1 : 0;
        if (reflState[i] === rk) continue;
        reflState[i] = rk;
        const c = REFL[rk]!;
        refl.setColorAt(i, col.setRGB(c[0], c[1], c[2]));
        rDirty = true;
      }
      if (dirty && mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      if (rDirty && refl.instanceColor) refl.instanceColor.needsUpdate = true;
    },
    dispose() {
      scene.remove(mesh, refl);
      geo.dispose();
      mat.dispose();
      mesh.dispose();
      rGeo.dispose();
      rMat.dispose();
      refl.dispose();
    },
  };
}
